import kinoraLogo from '@shared-assets/kinora_logo.svg'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { ShieldCheck, Sparkles, Wallet, Play, Heart, Clapperboard, Upload, ArrowRight } from 'lucide-react'
import BackgroundBeams from '@/components/aceternity/BackgroundBeams'
import ScrollReveal from '@/components/aceternity/ScrollReveal'
import { Button } from '@/components/ui/button'

const About = () => {
  const features = [
    { title: 'Subscribe & Watch', desc: 'Stream unlimited indie films, series, and documentaries from creators worldwide.', icon: <Play className="w-5 h-5" /> },
    { title: 'Get Monthly Slices', desc: 'Every plan includes slices — digital tokens worth $1 each, refreshed every month.', icon: <Wallet className="w-5 h-5" /> },
    { title: 'Fund Creators Directly', desc: 'Donate your slices to the creators and projects you believe in. 100% reaches them.', icon: <Heart className="w-5 h-5" /> },
    { title: 'Back What Gets Made', desc: 'Your support decides which films and series get funded and finished.', icon: <Sparkles className="w-5 h-5" /> },
  ]

  const principles = [
    { title: 'Transparent Economics', desc: 'Every slice is traceable. 100% of donations go to creators — Kinora takes no cut of creator support.' },
    { title: 'Secure Platform', desc: 'Enterprise-grade security protects your data, payments, and payouts.' },
    { title: 'Creator-First Analytics', desc: 'Creators see exactly who supports them and how their projects perform.' },
  ]

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <BackgroundBeams />
        <div className="max-w-5xl mx-auto px-6 pt-16 pb-10 md:pt-24 md:pb-16 text-center">
          <ScrollReveal className="flex flex-col items-center gap-4">
            <img src={kinoraLogo} alt="Kinora" className="h-14 w-auto md:h-16 select-none animate-[floatSlow_6s_ease-in-out_infinite]" draggable={false} />
            <Badge variant="secondary" className="bg-primary/15 text-primary border-primary/20">About Kinora</Badge>
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-tight">
              Streaming That Funds the People Who Make It
            </h1>
            <p className="text-muted-foreground text-base md:text-lg max-w-2xl">
              Kinora is an indie streaming platform where your subscription does more than unlock
              content — it directly funds the creators you believe in.
            </p>
          </ScrollReveal>
        </div>
      </section>

      <Separator className="bg-border/60" />

      {/* Mission */}
      <section className="max-w-5xl mx-auto px-6 py-10 md:py-14">
        <ScrollReveal>
          <Card className="bg-card/70 border-muted">
            <CardHeader>
              <CardTitle>Our Mission</CardTitle>
              <CardDescription>
                Give independent creators fair, transparent economics — and give viewers a real
                say in what gets made.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-sm md:text-base text-muted-foreground">
              Most streaming platforms pay creators through opaque algorithms. Kinora works
              differently: every subscription includes slices, and you choose which creators and
              projects receive them. Great content gets rewarded because the audience says so —
              directly, not through a black box.
            </CardContent>
          </Card>
        </ScrollReveal>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-6 pb-2 md:pb-4">
        <h2 className="text-2xl md:text-3xl font-bold mb-6">How Kinora Works</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {features.map((f, i) => (
            <ScrollReveal key={f.title} delay={i * 120}>
              <Card className="bg-card/70 border-muted hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5">
                <CardContent className="p-5">
                  <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center mb-3">
                    {f.icon}
                  </div>
                  <h3 className="font-semibold mb-1">{f.title}</h3>
                  <p className="text-sm text-muted-foreground">{f.desc}</p>
                </CardContent>
              </Card>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* The ecosystem */}
      <section className="max-w-6xl mx-auto px-6 py-10 md:py-14">
        <ScrollReveal>
          <h2 className="text-2xl md:text-3xl font-bold mb-2">One account. The whole ecosystem.</h2>
          <p className="text-muted-foreground mb-6 max-w-2xl">
            Kinora is where indie work gets funded, made, and released — three apps, one account.
          </p>
        </ScrollReveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {[
            {
              icon: <Play className="w-5 h-5" />,
              title: 'Kinora',
              tagline: 'Watch & fund it',
              desc: 'Stream unlimited indie titles, donate monthly slices, and back crowdfunding campaigns.',
              cta: 'Browse',
              href: '/browse',
            },
            {
              icon: <Clapperboard className="w-5 h-5" />,
              title: 'Kinora Guild',
              tagline: 'Make it',
              desc: 'The creator network — craft profiles, an open-roles crew board, and verified credits.',
              cta: 'About the Guild',
              href: '/guild',
            },
            {
              icon: <Upload className="w-5 h-5" />,
              title: 'Kinora Studio',
              tagline: 'Publish it',
              desc: 'Found a studio, upload your work, and release it to the Kinora catalogue.',
              cta: 'Publish',
              href: 'https://publish.slicetv.app',
            },
          ].map((app, i) => (
            <ScrollReveal key={app.title} delay={i * 120}>
              <Card className="bg-card/70 border-muted hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5 h-full">
                <CardContent className="p-5 flex flex-col h-full">
                  <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center mb-3">
                    {app.icon}
                  </div>
                  <p className="text-[10px] font-bold text-primary/60 uppercase tracking-widest">{app.tagline}</p>
                  <h3 className="font-semibold mt-0.5 mb-1">{app.title}</h3>
                  <p className="text-sm text-muted-foreground flex-1">{app.desc}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4 w-full group"
                    onClick={() =>
                      app.href.startsWith('http')
                        ? window.open(app.href, '_blank', 'noopener,noreferrer')
                        : (window.location.href = app.href)
                    }
                  >
                    {app.cta}
                    <ArrowRight className="w-3.5 h-3.5 ml-1.5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* Audience/Creator sections */}
      <section className="max-w-6xl mx-auto px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
          <ScrollReveal>
            <Card className="bg-card/70 border-muted">
            <CardHeader>
              <CardTitle>For Creators</CardTitle>
              <CardDescription>
                Direct funding, deeper engagement, and an audience that backs you.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-3">
              <p>
                When viewers donate slices to your work, you receive real, direct support —
                100% of every slice, with no platform cut on donations.
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>100% of slice donations go straight to you</li>
                <li>Crowdfunding campaigns for your next project</li>
                <li>Actionable analytics on your most valued content</li>
                <li>A community invested in seeing your work succeed</li>
              </ul>
            </CardContent>
            </Card>
          </ScrollReveal>

          <ScrollReveal delay={120}>
            <Card className="bg-card/70 border-muted">
            <CardHeader>
              <CardTitle>For Viewers</CardTitle>
              <CardDescription>
                Your subscription shapes what gets made.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-3">
              <p>
                Slices turn watching into participation. Fund the creators you love and help
                decide which projects get finished.
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Monthly slices included with every plan</li>
                <li>Discover curated indie films, series, and documentaries</li>
                <li>Connect directly with your favorite creators</li>
                <li>Help decide what gets funded and featured</li>
              </ul>
            </CardContent>
            </Card>
          </ScrollReveal>
        </div>
      </section>

      {/* Principles */}
      <section className="max-w-6xl mx-auto px-6 pb-10 md:pb-14">
        <ScrollReveal>
          <Card className="bg-card/70 border-muted">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-primary" /> Our Principles
            </CardTitle>
            <CardDescription>
              Fair, transparent, and secure by design.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {principles.map((t) => (
              <div key={t.title} className="p-4 rounded-lg bg-secondary/30 border border-border/40">
                <h4 className="font-semibold mb-1">{t.title}</h4>
                <p className="text-sm text-muted-foreground">{t.desc}</p>
              </div>
            ))}
          </CardContent>
          </Card>
        </ScrollReveal>
      </section>

      {/* CTA */}
      <section className="relative">
        <div className="max-w-6xl mx-auto px-6 pb-16">
          <ScrollReveal>
            <Card className="bg-card/80 border-muted overflow-hidden">
              <CardContent className="p-6 md:p-10">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="text-center md:text-left">
                  <h3 className="text-2xl md:text-3xl font-bold">Join the Kinora community</h3>
                  <p className="text-muted-foreground mt-1">Create an account and start watching in seconds.</p>
                </div>
                <div className="flex gap-3">
                  <Button size="lg" onClick={() => (window.location.href = '/auth')}>Get Started</Button>
                  <Button size="lg" variant="outline" onClick={() => (window.location.href = '/browse')}>Explore</Button>
                </div>
              </div>
              </CardContent>
            </Card>
          </ScrollReveal>
        </div>
      </section>
    </div>
  )
}

export default About
